<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>予約詳細</template>
      <template #right v-if="canEditCalendarSetting && booking">
        <FlatButton
          :class="$style.cancelButton"
          variant="caution"
          :disabled="
            !booking || !!booking.cancelReason || !!booking.isBookedByV2User
          "
          @click="openCancelBookingModal"
        >
          キャンセル
        </FlatButton>
      </template>
    </SettingsPagePanelHeader>
    <div v-if="booking" :class="$style.container">
      <div :class="$style.summary">
        <DolphinChip
          v-if="bookingStatus"
          :class="[$style.statusChip, $style[`statusChip--${bookingStatus}`]]"
        >
          {{ bookingStatusString[bookingStatus] }}
        </DolphinChip>
        <h2 :class="$style.summaryText">{{ booking.summary }}</h2>
        <FlatButton
          :class="$style.nextBookButton"
          variant="primary"
          :disabled="!!booking?.isBookedByV2User"
          @click="openNextBookModal"
        >
          次回面談予約
        </FlatButton>
      </div>
      <div :class="$style.plan">
        {{ dateStringForDisplay(booking) }}
        <div v-if="booking.cancelReason">
          {{ cancelReasonString[booking.cancelReason] }}
        </div>
      </div>
      <div>
        <span :class="$style.label">担当メンバー: </span>
        <router-link
          v-if="booking.assignedStaff.guid"
          :to="{
            name: 'memberManagementSettingDetail',
            params: {
              memberManagementSettingId: booking.assignedStaff.guid,
              entranceGUID: route.params.entranceGUID,
            },
          }"
        >
          {{ booking.assignedStaff.displayName }}
        </router-link>
      </div>
      <div>
        <span :class="$style.label">Web会議URL: </span>
        <ExternalLink
          v-if="booking.meetingURL"
          :href="booking.meetingURL"
          :class="$style.externalLink"
        >
          {{ booking.meetingURL }} </ExternalLink
        ><span v-else>なし</span>
      </div>
      <div v-if="booking.description" :class="$style.description">
        <span :class="$style.label">詳細:(予約時点の情報)</span>
        <div :class="$style.description__inner">
          {{ booking.description }}
        </div>
      </div>
      <div :class="$style.userInfo">
        <span :class="$style.label"
          >オカネコユーザー: {{ booking.okanekoUser ? '◯' : '×' }}</span
        >
        <div v-if="booking.okanekoUser" :class="$style.userInfo__inner">
          <p>ユーザーID: {{ booking.okanekoUser.id }}</p>
          <p>ニックネーム: {{ booking.okanekoUser.nickname }}</p>
          <p>
            チャンネル: <ChannelChip :channel="booking.okanekoUser.channel" />
          </p>
          <p>
            カルテ:
            <span v-if="!fetchedKarte">なし</span>
            <span v-else-if="fetchedKarte.hasError">{{
              fetchedKarte.errorMessage
            }}</span>
            <router-link
              v-else
              :to="{
                name: 'karteDetail',
                params: {
                  karteId: fetchedKarte.id,
                  entranceGUID: route.params.entranceGUID,
                },
              }"
              >あり</router-link
            >
          </p>
          <p>
            チャットルーム:
            <router-link
              v-if="booking.okanekoUser.chatRoom?.id"
              :to="{
                name: 'ownChat',
                params: {
                  roomId: booking.okanekoUser.chatRoom?.id,
                  entranceGUID: route.params.entranceGUID,
                },
              }"
              >あり</router-link
            >
            <span v-else>なし</span>
          </p>
        </div>
      </div>
      <CalendarBookingViewQuestionAnswers
        v-if="booking.questionAnswers"
        :question-answers="booking.questionAnswers"
      />
      <div>
        <span :class="$style.label">予約作成日時: </span
        ><span>{{ bookingCreatedAtString }}</span>
      </div>
      <div :class="$style.additionalInfo">
        予約ID: {{ booking.guid }}<br />
        ユーザーGUID: {{ booking.userGUID }}
      </div>
    </div>
    <CancelBookingModal>
      <CancelBookingModalInner
        :bookingGUID="route.params.bookingGUID"
        @submit="onCancelBookingSubmit"
        @cancel="onCancelBookingCancel"
      />
    </CancelBookingModal>
    <NextBookModal>
      <NextBookModalInner
        :bookingGUID="route.params.bookingGUID"
        @cancel="onNextBookCancel"
      />
    </NextBookModal>
  </SettingsPagePanel>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { format } from 'date-fns'
import ja from 'date-fns/locale/ja'
import ChannelChip from '@/components/atoms/ChannelChip.vue'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import { useModal } from '@/hooks/useModal'
import { useFetchBookingQuery } from './SettingsPageCalendarBookingView.generated.ts'
import { useCalendarSettingsPageQuery } from '../SettingsPageCalendarView/SettingsPageCalendarView.generated.ts'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import CancelBookingModalInner from './CancelBookingModal/CancelBookingModal.vue'
import NextBookModalInner from './NextBookModal/NextBookModal.vue'
import { cancelReasonString, dateStringForDisplay } from './booking.ts'
import { useStaff } from '@/hooks/useStaff.ts'
import CalendarBookingViewQuestionAnswers from '../SettingsPageCalendarView/questions/CalendarBookingViewQuestionAnswers/CalendarBookingViewQuestionAnswers.vue'
import { APIError } from '@/libs/utilities/CustomErrors.ts'
import { captureException } from '@/newrelic.ts'

const route = useRoute('calendarSettingBooking')
const {
  refetch: calendarBookingQueryRefetch,
  result: calendarBookingQueryResult,
  error: calendarBookingQueryError,
} = useFetchBookingQuery(
  {
    guid: route.params.bookingGUID as string,
  },
  { fetchPolicy: 'no-cache' }
)

const booking = computed(() => {
  if (calendarBookingQueryError.value) {
    captureException(calendarBookingQueryError.value)
    throw new APIError(500, 'エラーが発生しました')
  }

  const bookingResult = calendarBookingQueryResult.value?.bookingV2
  if (!bookingResult) {
    return undefined
  }

  switch (bookingResult.__typename) {
    case 'BookingErrorNotFound':
      throw new APIError(404, '予約が見つかりません')
    case 'BookingErrorPermissionDenined':
      throw new APIError(403, 'この予約の閲覧は許可されていません')
    case 'Booking':
      return bookingResult
    default:
      return undefined
  }
})

type FetchedKarte =
  | {
      hasError: false
      id: string
    }
  | {
      hasError: true
      errorMessage: string
    }

const fetchedKarte = computed<FetchedKarte | null>(() => {
  if (!booking.value) return null
  if (!booking.value.okanekoUser?.karteV2) return null

  const karte = booking.value.okanekoUser.karteV2

  switch (karte.__typename) {
    case 'Karte':
      return { hasError: false, id: karte.id }
    case 'UserKarteErrorPermissionDenied':
      return { hasError: true, errorMessage: '閲覧権限がありません' }
    default:
      return null
  }
})

type BookingStatus = 'open' | 'canceled'

const bookingStatus = computed((): BookingStatus | null => {
  if (!booking.value) {
    return null
  }

  if (booking.value.cancelReason) {
    return 'canceled'
  }

  if (new Date(booking.value.startsAt) > new Date()) {
    return 'open'
  }

  return null
})

const bookingStatusString: Record<BookingStatus, string> = {
  open: '開催前',
  canceled: 'キャンセル済み',
}

const { openCancelBookingModal, closeCancelBookingModal, CancelBookingModal } =
  useModal('CancelBooking')

const onCancelBookingSubmit = () => {
  calendarBookingQueryRefetch()
  closeCancelBookingModal()
}
const onCancelBookingCancel = () => closeCancelBookingModal()

const { openNextBookModal, closeNextBookModal, NextBookModal } =
  useModal('NextBook')

const onNextBookCancel = () => closeNextBookModal()

const { currentStaff } = useStaff()

const { result: calendarSettingsPageQueryResult } =
  useCalendarSettingsPageQuery({ fetchPolicy: 'cache-first' })

const canEditCalendarSetting = computed(() => {
  if (!currentStaff.value) {
    return false
  }

  if (!calendarSettingsPageQueryResult.value) {
    return false
  }

  return (
    calendarSettingsPageQueryResult.value.uiControl
      .editOkanekoCalendarSetting ||
    currentStaff.value?.guid === booking.value?.assignedStaff.guid
  )
})

const bookingCreatedAtString = computed(() =>
  booking.value
    ? format(new Date(booking.value.createdAt), 'yyyy年M月d日(E) HH:mm', {
        locale: ja,
      })
    : ''
)
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-weight: bold;
}

.summary {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  gap: 8px;
}

.statusChip {
  flex-shrink: 0;
  height: 31.5px;
  color: $color-text-and-object-high-emphasis-inverse;

  &--open {
    background-color: $color-label-green;
  }

  &--canceled {
    background-color: $color-label-gray;
  }
}

.summaryText {
  flex-grow: 1;
  word-break: break-all;
}

.nextBookButton {
  flex-shrink: 0;
  height: 21px;
}

.plan {
  display: flex;
  flex-direction: row;
  gap: 16px;
  font-weight: bold;
}

.description {
  &__inner {
    white-space: pre-wrap;
    padding: 8px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-inner;
  }
}

.externalLink {
  color: $color-text-and-object-accent-primary;

  &:hover {
    color: $color-text-and-object-accent-primary;
  }
}

.userInfo {
  &__inner {
    padding: 8px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-inner;
  }
}

.cancelButton {
  max-width: 70px;
}

.additionalInfo {
  margin-top: 16px;
  color: $color-text-and-object-low-emphasis;
  font-size: 10px;
}
</style>
